import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  ariaLabel?: string;
  children: ReactNode;
}

const SHEET_EASE = [0.32, 0.72, 0, 1] as const;

export default function Sheet({ open, onClose, title, ariaLabel, children }: SheetProps) {
  const { t } = useTranslation();
  // Portalled to the body because the triggers live inside transformed ancestors
  // (the lobby view slides on `x`), and a transform makes itself the containing
  // block for `position: fixed` — the overlay would otherwise be pinned to the
  // ancestor's box instead of the viewport.
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            onClick={onClose}
            aria-hidden="true"
          />
          {/*
            Portalling to the body escapes the app shell's max-w-[430px] frame
            (its transform made it the containing block), so the cap is
            re-applied here — inset-x-0 + mx-auto centres without a transform,
            which framer-motion's `y` animation would otherwise overwrite.
          */}
          <motion.div
            className="fixed inset-x-0 top-0 bottom-0 z-50 w-full max-w-[440px] mx-auto rounded-t-[16px] bg-[#222429] border-t border-[#2E3441] pt-[16px] text-white will-change-transform flex flex-col"
            style={{ boxShadow: '0px 5px 11.2px 0px #00000075 inset' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.32, ease: SHEET_EASE }}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel ?? title}
          >
            <div className="relative flex items-center justify-center pb-[16px] px-6 border-b border-[#2E3441] shrink-0">
              <h2 className="font-poppins font-semibold text-[16px] leading-none uppercase text-center">
                {title}
              </h2>
              <button
                type="button"
                aria-label={t('app.close')}
                onClick={onClose}
                // Pulled up by half its own height less half the title's, so the
                // 40px tap target centres on the 16px title line rather than
                // sitting below it — the title is the only thing in flow here.
                className="absolute right-[8px] top-0 -translate-y-[12px] flex items-center justify-center w-[40px] h-[40px] transition-transform duration-150 active:scale-90"
              >
                <img src="/cross.svg" alt="" className="w-[14px] h-[14px]" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom,0)+24px)]">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
